<template>
    <div class="portal-card-wrapper portal-card-wrapper--large">
        <div class="portal-card">
            <div class="upload-card-header">
                <h2 class="upload-heading">
                    <common-icon name="chose" size="small" class="header-upload-icon"></common-icon>
                    <span>{{ $t('pages.public.record_upload.step_upload.title') }}</span>
                </h2>
                <p class="upload-subtitle">
                    {{ $t('pages.public.record_upload.step_upload.uploading_for') }}
                    <strong class="patient-highlight">{{ patientName }}</strong>
                    —
                    {{ $t('pages.public.record_upload.step_upload.request_label') }}
                    <span class="req-id font-mono">{{ requestId }}</span>
                </p>
            </div>

            <div class="portal-card__body upload-body">
                <div
                    class="drop-zone-box"
                    :class="{ 'drop-zone-box--dragover': isDragging }"
                    @dragover.prevent="isDragging = true"
                    @dragleave.prevent="isDragging = false"
                    @drop.prevent="handleDrop"
                    @click="$refs.fileInput.click()"
                >
                    <input
                        id="portal-upload"
                        ref="fileInput"
                        type="file"
                        multiple
                        accept=".pdf,.tiff,.tif,.dcm,.png,.jpg"
                        class="file-input-hidden"
                        @change="handleSelect"
                    />
                    <common-icon name="chose" size="ultra-large" class="drop-zone-icon"></common-icon>
                    <p class="drop-main-text">
                        {{ $t('pages.public.record_upload.step_upload.drop_main') }}
                    </p>
                    <p class="drop-sub-text">
                        {{ $t('pages.public.record_upload.step_upload.drop_sub') }}
                    </p>
                </div>

                <div v-if="files.length > 0" class="selected-files-section">
                    <div class="selected-count-label">
                        {{ filesSelectedLabel }}
                    </div>

                    <div v-for="(fileItem, index) in files" :key="index" class="file-item-card">
                        <div class="file-item-left">
                            <common-icon name="file" size="medium" class="doc-icon"></common-icon>
                            <div class="file-info">
                                <div class="file-name">{{ fileItem.name }}</div>
                                <div class="file-size">{{ formatFileSize(fileItem.size) }}</div>
                            </div>
                        </div>

                        <div class="file-item-right">
                            <select v-model="fileItem.category" class="category-select">
                                <option v-for="cat in categoryOptions" :key="cat.value" :value="cat.value">
                                    {{ cat.label }}
                                </option>
                            </select>
                            <button
                                type="button"
                                class="file-delete-btn"
                                :title="$t('pages.public.record_upload.step_upload.remove_file')"
                                @click.stop="removeFile(index)"
                            >
                                ✕
                            </button>
                        </div>
                    </div>
                </div>

                <div class="note-section">
                    <label class="note-label" for="provider-note">
                        {{ $t('pages.public.record_upload.step_upload.note_label') }}
                        <span class="optional-text">{{ $t('pages.public.record_upload.step_upload.optional') }}</span>
                    </label>
                    <textarea
                        id="provider-note"
                        v-model="internalNotes"
                        class="note-textarea"
                        rows="3"
                        :placeholder="$t('pages.public.record_upload.step_upload.note_placeholder')"
                    ></textarea>
                </div>

                <div class="encryption-notice-box">
                    <common-icon name="lock" size="extra-small" class="enc-lock"></common-icon>
                    <div class="enc-text">
                        {{ $t('pages.public.record_upload.step_upload.encryption_notice') }}
                    </div>
                </div>
            </div>
        </div>

        <div class="step-nav-bar">
            <button type="button" class="nav-btn-back" @click="$emit('back')">
                <common-icon name="left-arrow" size="small"></common-icon>
                <span>{{ $t('common.microcopy.back') }}</span>
            </button>
            <button
                type="button"
                class="nav-btn-submit"
                :class="submitButtonClass"
                :disabled="isSubmitDisabled"
                @click="handleSubmit"
            >
                <common-icon name="send" size="small" class="send-plane-icon"></common-icon>
                <span>{{ submitButtonLabel }}</span>
            </button>
        </div>
    </div>
</template>

<script>
    export default {
        name: 'step-upload',
        props: {
            patientName: { type: String, default: 'Amos David' },
            requestId: { type: String, default: 'REQ001' },
            files: { type: Array, default: () => [] },
            notes: { type: String, default: '' },
            isUploading: { type: Boolean, default: false },
        },
        data() {
            return {
                isDragging: false,
                internalNotes: this.notes,
            }
        },
        computed: {
            isSubmitDisabled() {
                return this.files.length === 0 || this.isUploading
            },
            submitButtonLabel() {
                return this.isUploading
                    ? this.$t('pages.public.record_upload.step_upload.submitting')
                    : this.$t('pages.public.record_upload.step_upload.submit_securely')
            },
            submitButtonClass() {
                return {
                    'nav-btn-submit--active': !this.isSubmitDisabled,
                    'nav-btn-submit--disabled': this.isSubmitDisabled,
                }
            },
            filesSelectedLabel() {
                const count = this.files.length
                return this.$tc('pages.public.record_upload.step_upload.files_selected_count', count, {
                    count,
                })
            },
            categoryOptions() {
                return [
                    {
                        value: 'Complete Medical History',
                        label: this.$t('pages.public.record_upload.categories.complete_medical_history'),
                    },
                    {
                        value: 'Prescription History',
                        label: this.$t('pages.public.record_upload.categories.prescription_history'),
                    },
                    {
                        value: 'Lab Results',
                        label: this.$t('pages.public.record_upload.categories.lab_results'),
                    },
                    {
                        value: 'Surgical Records',
                        label: this.$t('pages.public.record_upload.categories.surgical_records'),
                    },
                    {
                        value: 'Imaging Reports',
                        label: this.$t('pages.public.record_upload.categories.imaging_reports'),
                    },
                    {
                        value: 'Immunization Records',
                        label: this.$t('pages.public.record_upload.categories.immunization_records'),
                    },
                    {
                        value: 'Consultation Notes',
                        label: this.$t('pages.public.record_upload.categories.consultation_notes'),
                    },
                    {
                        value: 'Mental Health Records',
                        label: this.$t('pages.public.record_upload.categories.mental_health_records'),
                    },
                    {
                        value: 'Other',
                        label: this.$t('pages.public.record_upload.categories.other'),
                    },
                ]
            },
        },
        methods: {
            handleDrop(event) {
                this.isDragging = false
                const droppedFiles = Array.from(event.dataTransfer.files)
                this.$emit('add-files', droppedFiles)
            },
            handleSelect(event) {
                const selectedFiles = Array.from(event.target.files)
                this.$emit('add-files', selectedFiles)
                event.target.value = ''
            },
            removeFile(index) {
                this.$emit('remove-file', index)
            },
            handleSubmit() {
                this.$emit('submit')
            },
            formatFileSize(bytes) {
                if (!bytes) return '0.01 MB'
                const k = 1024
                const sizes = ['B', 'KB', 'MB', 'GB']
                const i = Math.floor(Math.log(bytes) / Math.log(k))
                return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
            },
        },
        watch: {
            internalNotes(val) {
                this.$emit('update:notes', val)
            },
        },
    }
</script>

<style scoped lang="scss">
    $brand-primary: #155dfc;
    $brand-primary-hover: #1048c7;
    $brand-border: #e2e8f0;

    .portal-card-wrapper {
        width: 100%;
    }

    .portal-card {
        background: #ffffff;
        border-radius: 16px;
        border: 1px solid $brand-border;
        box-shadow: 0 4px 20px -2px rgba(15, 23, 42, 0.05);
        overflow: hidden;

        &__body {
            padding: 24px;
        }
    }

    .upload-card-header {
        padding: 20px 24px;
        border-bottom: 1px solid #f1f5f9;
    }

    .upload-heading {
        font-size: 18px;
        font-weight: 700;
        color: #111827;
        margin: 0;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .header-upload-icon {
        color: #2563eb;
        display: inline-flex;
        align-items: center;
    }

    .upload-subtitle {
        font-size: 14px;
        color: #6b7280;
        margin: 4px 0 0 0;
    }

    .patient-highlight {
        color: #374151;
        font-weight: 600;
    }

    .req-id {
        font-family: monospace;
        color: #374151;
    }

    .upload-body {
        display: flex;
        flex-direction: column;
        gap: 20px;
    }

    .drop-zone-box {
        position: relative;
        border: 2px dashed #e2e8f0;
        border-radius: 16px;
        padding: 40px 24px;
        text-align: center;
        cursor: pointer;
        background: #ffffff;
        transition: all 0.2s ease;
        display: flex;
        flex-direction: column;
        align-items: center;

        &:hover,
        &--dragover {
            border-color: #93c5fd;
            background: #f9fafb;
        }
    }

    .drop-zone-icon {
        color: #d1d5db;
        margin-bottom: 12px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }

    .drop-main-text {
        font-size: 14px;
        font-weight: 600;
        color: #374151;
        margin: 0;
    }

    .drop-sub-text {
        font-size: 12px;
        color: #9ca3af;
        margin: 4px 0 0 0;
    }

    .file-input-hidden {
        display: none;
    }

    /* Selected Files */
    .selected-files-section {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .selected-count-label {
        font-size: 11px;
        font-weight: 700;
        color: #9ca3af;
        letter-spacing: 0.05em;
    }

    .file-item-card {
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 10px;
        padding: 12px 16px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
    }

    .file-item-left {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .doc-icon {
        color: #2563eb;
        display: inline-flex;
        align-items: center;
    }

    .file-info {
        display: flex;
        flex-direction: column;
    }

    .file-name {
        font-size: 13px;
        font-weight: 600;
        color: #0f172a;
    }

    .file-size {
        font-size: 11px;
        color: #94a3b8;
        margin-top: 2px;
    }

    .file-item-right {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .category-select {
        border: 1px solid #cbd5e1;
        border-radius: 8px;
        padding: 6px 12px;
        font-size: 13px;
        color: #334155;
        background: #ffffff;
        outline: none;
        cursor: pointer;

        &:focus {
            border-color: #155dfc;
        }
    }

    .file-delete-btn {
        background: none;
        border: none;
        color: #cbd5e1;
        cursor: pointer;
        font-size: 14px;
        padding: 4px;

        &:hover {
            color: #ef4444;
        }
    }

    .note-section {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .note-label {
        font-size: 14px;
        font-weight: 500;
        color: #374151;
        margin: 0;

        .optional-text {
            font-weight: 400;
            color: #9ca3af;
        }
    }

    .note-textarea {
        width: 100%;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        padding: 10px 14px;
        font-size: 14px;
        color: #1f2937;
        outline: none;
        resize: none;
        min-height: 80px;
        box-sizing: border-box;
        font-family: inherit;
        background: #ffffff;
        transition: border-color 0.2s ease, box-shadow 0.2s ease;

        &:focus {
            border-color: #2563eb;
            box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
        }
    }

    .encryption-notice-box {
        background: #eff6ff;
        border: 1px solid #bfdbfe;
        border-radius: 8px;
        padding: 12px 16px;
        display: flex;
        align-items: flex-start;
        gap: 12px;
        font-size: 14px;
        line-height: 1.5;
        color: #1e40af;

        .enc-lock {
            color: #2563eb;
            flex-shrink: 0;
            margin-top: 2px;
            display: inline-flex;
            align-items: center;
        }

        .enc-text {
            color: #1e40af;
            font-size: 14px;
            line-height: 1.5;
        }
    }

    /* Bottom Navigation */
    .step-nav-bar {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-top: 20px;
    }

    .nav-btn-back {
        background: #ffffff;
        border: 1px solid #cbd5e1;
        border-radius: 8px;
        padding: 10px 18px;
        font-size: 14px;
        font-weight: 600;
        color: #0f172a;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 6px;
        transition: background-color 0.15s ease;

        &:hover {
            background: #f1f5f9;
        }
    }

    .nav-btn-submit {
        flex: 1;
        border-radius: 8px;
        padding: 12px 20px;
        font-size: 14px;
        font-weight: 600;
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        transition: all 0.2s ease;

        &--active {
            background-color: $brand-primary;
            color: #ffffff;

            &:hover {
                background-color: $brand-primary-hover;
            }
        }

        &--disabled,
        &:disabled {
            background-color: #8da4f7;
            color: #ffffff;
            cursor: not-allowed;
            opacity: 0.9;
        }
    }

    .send-plane-icon {
        display: inline-flex;
        align-items: center;
    }
</style>
